import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search, Globe, CheckCircle, XCircle, AlertTriangle, Info,
  Loader2, Download, RefreshCw, ExternalLink, ChevronDown, ChevronRight,
  FileText, Zap, Link2, Shield, Smartphone, Code, Image, Clock, History, Trash2, Eye
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import toast from 'react-hot-toast';
import { competitorService } from '../../services/competitorService';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

interface AuditIssue {
  id: string;
  title: string;
  description: string;
  severity: 'critical' | 'warning' | 'info' | 'passed';
  category: string;
  recommendation: string;
  affectedUrls?: string[];
}

interface AuditCategory {
  name: string;
  icon: React.ReactNode;
  score: number;
  issues: AuditIssue[];
  expanded: boolean;
}

interface CoreWebVital {
  value: number;
  displayValue: string;
  score: number;
}

interface Screenshot {
  final: string | null;
  timing: number | null;
  thumbnails: Array<{
    timing: number;
    timestamp: number;
    data: string;
  }>;
}

interface DeviceData {
  overallScore: number;
  categories: AuditCategory[];
  totalIssues: { critical: number; warning: number; info: number; passed: number };
  coreWebVitals?: {
    lcp: CoreWebVital;
    fid: CoreWebVital;
    cls: CoreWebVital;
    fcp: CoreWebVital;
    ttfb: CoreWebVital;
    speedIndex: CoreWebVital;
    tbt: CoreWebVital;
    tti: CoreWebVital;
  };
  screenshot?: Screenshot;
}

interface SEOAnalysis {
  score: number;
  loadTime: number;
  contentLength: number;
  meta: {
    title: string;
    titleLength: number;
    description: string;
    descriptionLength: number;
    keywords: string;
    robots: string;
    canonical: string;
    ogTitle: string;
    ogDescription: string;
    ogImage: string;
    twitterCard: string;
  };
  headings: {
    h1: string[];
    h2: string[];
    h3: string[];
    h4: string[];
    h1Count: number;
    h2Count: number;
    h3Count: number;
    h4Count: number;
  };
  links: {
    internalCount: number;
    externalCount: number;
    internal: Array<{ href: string; text: string; rel: string }>;
    external: Array<{ href: string; text: string; rel: string }>;
  };
  images: {
    total: number;
    withAlt: number;
    withoutAlt: number;
    list: Array<{ src: string; alt?: string; hasAlt: boolean }>;
  };
  content: {
    wordCount: number;
    paragraphs: number;
    textToHtmlRatio: number;
  };
  technical: {
    hasViewport: boolean;
    hasCharset: boolean;
    hasLangAttribute: boolean;
    usesHttps: boolean;
    hasStructuredData: boolean;
    hasFavicon: boolean;
  };
  issues: Array<{ type: string; code: string; message: string }>;
}

interface AuditResult {
  url: string;
  overallScore: number;
  categories: AuditCategory[];
  totalIssues: { critical: number; warning: number; info: number; passed: number };
  auditedAt: string;
  pageSpeed: { mobile: number; desktop: number };
  scores?: {
    mobile: { performance: number; accessibility: number; bestPractices: number; seo: number };
    desktop: { performance: number; accessibility: number; bestPractices: number; seo: number };
  };
  coreWebVitals?: {
    lcp: CoreWebVital;
    fid: CoreWebVital;
    cls: CoreWebVital;
    fcp: CoreWebVital;
    ttfb: CoreWebVital;
    speedIndex: CoreWebVital;
    tbt: CoreWebVital;
    tti: CoreWebVital;
  };
  screenshot?: Screenshot;
  lighthouseVersion?: string;
  mobile?: DeviceData;
  desktop?: DeviceData;
  seoAnalysis?: SEOAnalysis;
}

interface AuditHistoryItem {
  id: string;
  url: string;
  status: string;
  scores: {
    overall: number;
    seo: number;
    performance: number;
    accessibility: number;
    bestPractices: number;
  };
  total_issues: number;
  critical_issues: number;
  warnings: number;
  info_issues: number;
  page_speed_mobile: number;
  page_speed_desktop: number;
  lighthouse_version: string;
  created_at: string;
  completed_at: string;
}

const SiteAuditPage: React.FC = () => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [auditResult, setAuditResult] = useState<AuditResult | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [selectedDevice, setSelectedDevice] = useState<'mobile' | 'desktop'>('mobile');

  // History states
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<AuditHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  const [loadingAuditId, setLoadingAuditId] = useState<string | null>(null);

  // Get current device data based on selected device
  const currentData = (() => {
    if (!auditResult) return null;

    const deviceData = selectedDevice === 'mobile' ? auditResult.mobile : auditResult.desktop;

    // Se tem dados específicos do device, usa eles
    if (deviceData) {
      return deviceData;
    }

    // Fallback: criar dados baseados nos scores separados
    const scores = selectedDevice === 'mobile'
      ? auditResult.scores?.mobile
      : auditResult.scores?.desktop;

    // Pegar screenshot do device correto se disponível
    const screenshot = selectedDevice === 'mobile'
      ? (auditResult.mobile?.screenshot || auditResult.screenshot)
      : (auditResult.desktop?.screenshot || auditResult.screenshot);

    // Pegar Core Web Vitals do device correto se disponível
    const coreWebVitals = selectedDevice === 'mobile'
      ? (auditResult.mobile?.coreWebVitals || auditResult.coreWebVitals)
      : (auditResult.desktop?.coreWebVitals || auditResult.coreWebVitals);

    // Calcula overall score baseado nos scores do device selecionado
    const overallScore = scores
      ? Math.round((scores.performance * 0.3) + (scores.seo * 0.3) + (scores.accessibility * 0.2) + (scores.bestPractices * 0.2))
      : auditResult.overallScore;

    return {
      overallScore,
      categories: auditResult.categories,
      totalIssues: auditResult.totalIssues,
      coreWebVitals,
      screenshot,
      scores
    };
  })();

  // Load audit history
  const loadHistory = useCallback(async (page: number = 1) => {
    setHistoryLoading(true);
    try {
      const response = await competitorService.getAuditHistory({ page, limit: 10 });
      if (response.success) {
        setHistory(response.data);
        setHistoryTotal(response.pagination?.total || 0);
        setHistoryTotalPages(response.pagination?.totalPages || 0);
        setHistoryPage(page);
      }
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error(t('siteAudit.historyError', 'Error loading history'));
    } finally {
      setHistoryLoading(false);
    }
  }, [t]);

  // Load specific audit from history
  const loadAuditFromHistory = useCallback(async (auditId: string) => {
    setLoadingAuditId(auditId);
    try {
      const response = await competitorService.getAuditById(auditId);

      if (response.success && response.data) {
        const result = response.data;

        // Add icons to categories
        const categoryIcons: Record<string, React.ReactNode> = {
          'SEO': <FileText className="w-5 h-5" />,
          'SEO On-Page': <FileText className="w-5 h-5" />,
          'Performance': <Zap className="w-5 h-5" />,
          'Links': <Link2 className="w-5 h-5" />,
          'Security': <Shield className="w-5 h-5" />,
          'Seguranca': <Shield className="w-5 h-5" />,
          'Accessibility': <Smartphone className="w-5 h-5" />,
          'Mobile': <Smartphone className="w-5 h-5" />,
          'Tecnico': <Code className="w-5 h-5" />
        };

        const addIconsToCategories = (categories: any[]) => categories?.map((cat: any) => ({
          ...cat,
          icon: categoryIcons[cat.name] || <FileText className="w-5 h-5" />,
          expanded: false
        })) || [];

        const processedResult = {
          ...result,
          categories: addIconsToCategories(result.categories || []),
          mobile: result.mobile ? {
            ...result.mobile,
            categories: addIconsToCategories(result.mobile.categories || [])
          } : undefined,
          desktop: result.desktop ? {
            ...result.desktop,
            categories: addIconsToCategories(result.desktop.categories || [])
          } : undefined
        };

        setAuditResult(processedResult);
        setUrl(result.url || '');
        setShowHistory(false);

        const criticalCategories = new Set<string>(
          (processedResult.categories || []).filter((cat: AuditCategory) => cat.issues?.some(i => i.severity === 'critical')).map((cat: AuditCategory) => cat.name)
        );
        setExpandedCategories(criticalCategories);
      }
    } catch (error) {
      console.error('Error loading audit:', error);
      toast.error(t('siteAudit.loadError', 'Error loading audit'));
    } finally {
      setLoadingAuditId(null);
    }
  }, [t]);

  // Delete audit from history
  const deleteAuditFromHistory = useCallback(async (auditId: string) => {
    if (!confirm(t('siteAudit.deleteConfirm', 'Are you sure you want to delete this audit?'))) return;

    try {
      const response = await competitorService.deleteAudit(auditId);
      if (response.success) {
        toast.success(t('siteAudit.deleteSuccess', 'Audit deleted successfully'));
        loadHistory(historyPage);
      }
    } catch (error) {
      console.error('Error deleting audit:', error);
      toast.error(t('siteAudit.deleteError', 'Error deleting audit'));
    }
  }, [t, historyPage, loadHistory]);

  // Load history when showing
  useEffect(() => {
    if (showHistory && history.length === 0) {
      loadHistory(1);
    }
  }, [showHistory, history.length, loadHistory]);

  const handleAudit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!url.trim()) return;

    setIsLoading(true);
    try {
      // Import polling service
      const { pollJobUntilComplete } = await import('../../services/aiJobService');

      // Call audit with async mode
      const response = await competitorService.runSiteAudit(url.trim(), true, true);

      let result = null;

      // If async mode, poll for result
      if (response.async && response.jobId) {
        toast.loading(t('siteAudit.runningInBackground', 'Executando auditoria em background...'), { id: 'audit' });

        const jobResult = await pollJobUntilComplete(
          response.jobId,
          undefined,
          2000,
          180000 // 3 minute timeout
        );

        toast.dismiss('audit');
        result = jobResult;
      } else if (response.success && response.data) {
        result = response.data;
      }

      if (result) {
        // Add icons to categories (they can't come from backend)
        const categoryIcons: Record<string, React.ReactNode> = {
          'SEO': <FileText className="w-5 h-5" />,
          'SEO On-Page': <FileText className="w-5 h-5" />,
          'Performance': <Zap className="w-5 h-5" />,
          'Links': <Link2 className="w-5 h-5" />,
          'Security': <Shield className="w-5 h-5" />,
          'Seguranca': <Shield className="w-5 h-5" />,
          'Accessibility': <Smartphone className="w-5 h-5" />,
          'Mobile': <Smartphone className="w-5 h-5" />,
          'Tecnico': <Code className="w-5 h-5" />
        };

        // Helper function to add icons to categories
        const addIconsToCategories = (categories: any[]) => categories.map((cat: any) => ({
          ...cat,
          icon: categoryIcons[cat.name] || <FileText className="w-5 h-5" />,
          expanded: false
        }));

        const categoriesWithIcons = addIconsToCategories(result.categories || []);

        // Process mobile and desktop data with icons
        const processedResult = {
          ...result,
          categories: categoriesWithIcons,
          mobile: result.mobile ? {
            ...result.mobile,
            categories: addIconsToCategories(result.mobile.categories || [])
          } : undefined,
          desktop: result.desktop ? {
            ...result.desktop,
            categories: addIconsToCategories(result.desktop.categories || [])
          } : undefined
        };

        setAuditResult(processedResult);

        // Expand categories with critical issues by default
        const criticalCategories = new Set<string>(
          categoriesWithIcons.filter((cat: AuditCategory) => cat.issues.some(i => i.severity === 'critical')).map((cat: AuditCategory) => cat.name)
        );
        setExpandedCategories(criticalCategories);

        // Reload history to include the new audit
        if (showHistory) {
          loadHistory(1);
        } else {
          // Reset history so it reloads when opened
          setHistory([]);
        }

        toast.success(t('siteAudit.auditComplete', 'Auditoria concluída!'));
      } else {
        toast.error(t('siteAudit.auditFailed', 'Failed to run audit. Please try again.'));
        setAuditResult(null);
      }
    } catch (error: any) {
      console.error('Error running audit:', error);
      toast.dismiss('audit');
      const errorMessage = error.response?.data?.message || error.message || t('siteAudit.auditFailed', 'Failed to run audit. Please try again.');
      toast.error(errorMessage);
      setAuditResult(null);
    } finally {
      setIsLoading(false);
    }
  }, [url, t, showHistory, loadHistory]);

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 dark:text-green-400';
    if (score >= 70) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 50) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 70) return 'bg-yellow-500';
    if (score >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'passed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return null;
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <Badge variant="danger">{t('siteAudit.critical', 'Critical')}</Badge>;
      case 'warning':
        return <Badge variant="warning">{t('siteAudit.warning', 'Warning')}</Badge>;
      case 'info':
        return <Badge variant="primary">{t('siteAudit.info', 'Info')}</Badge>;
      case 'passed':
        return <Badge variant="success">{t('siteAudit.passed', 'OK')}</Badge>;
      default:
        return null;
    }
  };

  const [isExporting, setIsExporting] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const handleExportPDF = async () => {
    if (!auditResult || !currentData) return;

    setIsExporting(true);
    toast.loading(t('siteAudit.generatingPDF', 'Generating PDF...'), { id: 'pdf-export' });

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 15;
      let yPos = 20;

      // Title
      pdf.setFontSize(20);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Site Audit Report', margin, yPos);
      yPos += 10;

      // URL
      pdf.setFontSize(12);
      pdf.setTextColor(107, 114, 128);
      pdf.text(url, margin, yPos);
      yPos += 8;

      // Date
      pdf.setFontSize(10);
      pdf.text(`${t('common.date', 'Date')}: ${new Date().toLocaleString()}`, margin, yPos);
      yPos += 15;

      // Overall Score
      pdf.setFontSize(16);
      pdf.setTextColor(31, 41, 55);
      pdf.text(`${t('siteAudit.overallScore', 'Overall Score')}: ${currentData.overallScore}/100`, margin, yPos);
      yPos += 12;

      // Scores section
      pdf.setFontSize(14);
      pdf.text(t('siteAudit.categoryScores', 'Category Scores'), margin, yPos);
      yPos += 8;

      pdf.setFontSize(11);
      pdf.setTextColor(75, 85, 99);

      const deviceScores = selectedDevice === 'mobile' ? auditResult.scores?.mobile : auditResult.scores?.desktop;
      const scores = [
        { name: 'Performance', score: deviceScores?.performance || 0 },
        { name: 'SEO', score: deviceScores?.seo || 0 },
        { name: 'Accessibility', score: deviceScores?.accessibility || 0 },
        { name: 'Best Practices', score: deviceScores?.bestPractices || 0 }
      ];

      scores.forEach(item => {
        pdf.text(`• ${item.name}: ${item.score}/100`, margin + 5, yPos);
        yPos += 6;
      });
      yPos += 8;

      // Issues Summary
      pdf.setFontSize(14);
      pdf.setTextColor(31, 41, 55);
      pdf.text(t('siteAudit.issuesSummary', 'Issues Summary'), margin, yPos);
      yPos += 8;

      pdf.setFontSize(11);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`• ${t('siteAudit.critical', 'Critical')}: ${currentData.totalIssues?.critical || 0}`, margin + 5, yPos);
      yPos += 6;
      pdf.text(`• ${t('siteAudit.warning', 'Warning')}: ${currentData.totalIssues?.warning || 0}`, margin + 5, yPos);
      yPos += 6;
      pdf.text(`• ${t('siteAudit.info', 'Info')}: ${currentData.totalIssues?.info || 0}`, margin + 5, yPos);
      yPos += 6;
      pdf.text(`• ${t('siteAudit.passed', 'Passed')}: ${currentData.totalIssues?.passed || 0}`, margin + 5, yPos);
      yPos += 12;

      // Core Web Vitals
      if (currentData.coreWebVitals) {
        pdf.setFontSize(14);
        pdf.setTextColor(31, 41, 55);
        pdf.text('Core Web Vitals', margin, yPos);
        yPos += 8;

        pdf.setFontSize(11);
        pdf.setTextColor(75, 85, 99);
        if (currentData.coreWebVitals.lcp) {
          pdf.text(`• LCP (Largest Contentful Paint): ${currentData.coreWebVitals.lcp.displayValue}`, margin + 5, yPos);
          yPos += 6;
        }
        if (currentData.coreWebVitals.fid) {
          pdf.text(`• FID (First Input Delay): ${currentData.coreWebVitals.fid.displayValue}`, margin + 5, yPos);
          yPos += 6;
        }
        if (currentData.coreWebVitals.cls) {
          pdf.text(`• CLS (Cumulative Layout Shift): ${currentData.coreWebVitals.cls.displayValue}`, margin + 5, yPos);
          yPos += 6;
        }
        if (currentData.coreWebVitals.ttfb) {
          pdf.text(`• TTFB (Time to First Byte): ${currentData.coreWebVitals.ttfb.displayValue}`, margin + 5, yPos);
          yPos += 6;
        }
        yPos += 8;
      }

      // Issues by Category
      pdf.setFontSize(14);
      pdf.setTextColor(31, 41, 55);
      pdf.text(t('siteAudit.detailedIssues', 'Detailed Issues'), margin, yPos);
      yPos += 10;

      for (const category of currentData.categories || []) {
        if (yPos > 260) {
          pdf.addPage();
          yPos = 20;
        }

        pdf.setFontSize(12);
        pdf.setTextColor(31, 41, 55);
        pdf.text(`${category.name} (${category.score}/100)`, margin, yPos);
        yPos += 7;

        pdf.setFontSize(9);
        pdf.setTextColor(107, 114, 128);

        const criticalIssues = category.issues.filter(i => i.severity === 'critical');
        const warningIssues = category.issues.filter(i => i.severity === 'warning');

        [...criticalIssues, ...warningIssues].slice(0, 5).forEach(issue => {
          if (yPos > 270) {
            pdf.addPage();
            yPos = 20;
          }
          const severityIcon = issue.severity === 'critical' ? '❌' : '⚠️';
          const maxWidth = pageWidth - margin * 2 - 10;
          const lines = pdf.splitTextToSize(`${severityIcon} ${issue.title}`, maxWidth);
          pdf.text(lines, margin + 5, yPos);
          yPos += lines.length * 4 + 2;
        });
        yPos += 5;
      }

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(156, 163, 175);
      pdf.text('Generated by DevSkin Marketing Platform', margin, 290);

      // Save PDF
      const fileName = `site-audit-${url.replace(/[^a-z0-9]/gi, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      toast.success(t('siteAudit.exportSuccess', 'PDF exported successfully!'), { id: 'pdf-export' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(t('siteAudit.exportError', 'Failed to export PDF'), { id: 'pdf-export' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          {t('nav.sections.siteAudit', 'Auditoria do site')}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          {t('competitors.siteAuditDescription', 'Complete technical SEO analysis for any site')}
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent>
          <form onSubmit={handleAudit} className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <div className="flex-1 min-w-0">
              <Input
                placeholder={t('siteAudit.enterUrl', 'Enter the site URL (e.g.: https://example.com)')}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                leftIcon={<Globe className="w-5 h-5" />}
              />
            </div>
            <div className="flex gap-2 sm:gap-4">
              <Button type="submit" disabled={isLoading || !url.trim()} className="flex-1 sm:flex-none h-10 whitespace-nowrap">
                {isLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                <span className="sm:hidden">{t('siteAudit.startAuditShort', 'Auditar')}</span>
                <span className="hidden sm:inline">{t('siteAudit.startAudit', 'Start Audit')}</span>
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowHistory(!showHistory)}
                aria-label={t('siteAudit.history', 'History')}
                className="h-10 whitespace-nowrap flex-shrink-0"
              >
                <History className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('siteAudit.history', 'History')}</span>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* History Section */}
      {showHistory && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              {t('siteAudit.previousAudits', 'Previous Audits')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('siteAudit.noHistory', 'No previous audits found')}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto scrollbar-hide">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">URL</th>
                        <th className="text-center py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">{t('siteAudit.score', 'Score')}</th>
                        <th className="hidden sm:table-cell text-center py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">Mobile</th>
                        <th className="hidden sm:table-cell text-center py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">Desktop</th>
                        <th className="hidden sm:table-cell text-center py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">{t('siteAudit.issues', 'Issues')}</th>
                        <th className="hidden sm:table-cell text-center py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">{t('common.date', 'Date')}</th>
                        <th className="text-right py-3 px-2 text-sm font-medium text-gray-500 dark:text-gray-400">{t('common.actions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((item) => (
                        <tr key={item.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="py-3 px-2">
                            <span className="text-sm text-gray-900 dark:text-white truncate max-w-[160px] sm:max-w-xs block">
                              {item.url}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className={`font-bold ${getScoreColor(item.scores?.overall || 0)}`}>
                              {item.scores?.overall || 0}
                            </span>
                          </td>
                          <td className="hidden sm:table-cell py-3 px-2 text-center">
                            <span className={`text-sm ${getScoreColor(item.page_speed_mobile || 0)}`}>
                              {item.page_speed_mobile || 0}
                            </span>
                          </td>
                          <td className="hidden sm:table-cell py-3 px-2 text-center">
                            <span className={`text-sm ${getScoreColor(item.page_speed_desktop || 0)}`}>
                              {item.page_speed_desktop || 0}
                            </span>
                          </td>
                          <td className="hidden sm:table-cell py-3 px-2 text-center">
                            <div className="flex items-center justify-center gap-2 text-xs">
                              {item.critical_issues > 0 && (
                                <span className="text-red-500">{item.critical_issues} {t('siteAudit.critical', 'Critical')}</span>
                              )}
                              {item.warnings > 0 && (
                                <span className="text-yellow-500">{item.warnings} {t('siteAudit.warning', 'Warning')}</span>
                              )}
                            </div>
                          </td>
                          <td className="hidden sm:table-cell py-3 px-2 text-center text-sm text-gray-500 dark:text-gray-400">
                            {new Date(item.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => loadAuditFromHistory(item.id)}
                                disabled={loadingAuditId === item.id}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                                title={t('siteAudit.view', 'View')}
                              >
                                {loadingAuditId === item.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Eye className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => deleteAuditFromHistory(item.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                title={t('common.delete', 'Delete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {historyTotalPages > 1 && (
                  <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {t('common.showing', 'Showing')} {((historyPage - 1) * 10) + 1}-{Math.min(historyPage * 10, historyTotal)} {t('common.of', 'of')} {historyTotal}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={historyPage === 1}
                        onClick={() => loadHistory(historyPage - 1)}
                      >
                        {t('common.previous', 'Previous')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={historyPage === historyTotalPages}
                        onClick={() => loadHistory(historyPage + 1)}
                      >
                        {t('common.next', 'Next')}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <Loader2 className="w-16 h-16 animate-spin text-primary-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('siteAudit.analyzing', 'Analyzing site...')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {t('siteAudit.analyzingDesc', 'This may take a few seconds')}
              </p>
              <div className="mt-6 max-w-md mx-auto">
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>{t('siteAudit.checkingSEO', 'Checking On-Page SEO')}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                  <span>{t('siteAudit.checkingPerformance', 'Analyzing performance')}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-400 dark:text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>{t('siteAudit.checkingLinks', 'Checking links')}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {auditResult && !isLoading && currentData && (
        <>
          {/* Device Toggle */}
          <Card>
            <CardContent className="py-3">
              <div className="flex items-center justify-center gap-2">
                <span className="hidden sm:inline text-sm text-gray-500 dark:text-gray-400 mr-2">
                  {t('siteAudit.viewAs', 'View as')}:
                </span>
                <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                  <button
                    onClick={() => setSelectedDevice('mobile')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedDevice === 'mobile'
                        ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <Smartphone className="w-4 h-4" />
                    Mobile
                  </button>
                  <button
                    onClick={() => setSelectedDevice('desktop')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedDevice === 'desktop'
                        ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <Globe className="w-4 h-4" />
                    Desktop
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Score Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Overall Score */}
            <Card className="md:col-span-1">
              <CardContent className="pt-6 text-center">
                <div className="relative inline-flex items-center justify-center">
                  <svg className="w-32 h-32">
                    <circle
                      className="text-gray-200 dark:text-gray-700"
                      strokeWidth="10"
                      stroke="currentColor"
                      fill="transparent"
                      r="52"
                      cx="64"
                      cy="64"
                    />
                    <circle
                      className={getScoreColor(currentData.overallScore).replace('text-', 'text-')}
                      strokeWidth="10"
                      strokeDasharray={`${currentData.overallScore * 3.27} 327`}
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="transparent"
                      r="52"
                      cx="64"
                      cy="64"
                      style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
                    />
                  </svg>
                  <span className={`absolute text-3xl font-bold ${getScoreColor(currentData.overallScore)}`}>
                    {currentData.overallScore}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  {t('siteAudit.overallScore', 'Overall Score')}
                </p>
              </CardContent>
            </Card>

            {/* Issues Summary */}
            <Card className="md:col-span-2">
              <CardContent className="pt-6">
                <h3 className="font-medium text-gray-900 dark:text-white mb-4">
                  {t('siteAudit.issuesSummary', 'Issues Summary')}
                </h3>
                <div className="grid grid-cols-4 gap-2 sm:gap-4">
                  <div className="text-center">
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center mx-auto mb-1 sm:mb-2">
                      <XCircle className="w-4 h-4 sm:w-6 sm:h-6 text-red-500" />
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-red-600 dark:text-red-400">
                      {currentData.totalIssues.critical}
                    </p>
                    <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                      {t('siteAudit.criticalPlural', 'Critical')}
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center mx-auto mb-1 sm:mb-2">
                      <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-500" />
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                      {currentData.totalIssues.warning}
                    </p>
                    <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                      {t('siteAudit.warnings', 'Warnings')}
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mx-auto mb-1 sm:mb-2">
                      <Info className="w-4 h-4 sm:w-6 sm:h-6 text-blue-500" />
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {currentData.totalIssues.info}
                    </p>
                    <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                      {t('siteAudit.notices', 'Notices')}
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mx-auto mb-1 sm:mb-2">
                      <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-500" />
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                      {currentData.totalIssues.passed}
                    </p>
                    <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                      {t('siteAudit.passedPlural', 'Passed')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Page Speed */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium text-gray-900 dark:text-white mb-4">
                  {t('siteAudit.pageSpeed', 'Page Speed')}
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                        <Smartphone className="w-4 h-4" />
                        Mobile
                      </span>
                      <span className={`font-bold ${getScoreColor(auditResult.pageSpeed.mobile)}`}>
                        {auditResult.pageSpeed.mobile}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getScoreBgColor(auditResult.pageSpeed.mobile)}`}
                        style={{ width: `${auditResult.pageSpeed.mobile}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Desktop
                      </span>
                      <span className={`font-bold ${getScoreColor(auditResult.pageSpeed.desktop)}`}>
                        {auditResult.pageSpeed.desktop}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getScoreBgColor(auditResult.pageSpeed.desktop)}`}
                        style={{ width: `${auditResult.pageSpeed.desktop}%` }}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Screenshot and Core Web Vitals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Screenshot */}
            {currentData.screenshot?.final && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Image className="w-5 h-5" />
                    {t('siteAudit.screenshot', 'Site Screenshot')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                    <img
                      src={currentData.screenshot.final}
                      alt={t('siteAudit.screenshotAlt', 'Screenshot of {{url}}', { url: auditResult.url })}
                      className="w-full h-auto rounded-lg shadow-inner"
                    />
                    {currentData.screenshot.timing && (
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {(currentData.screenshot.timing / 1000).toFixed(1)}s
                      </div>
                    )}
                  </div>
                  {/* Thumbnails Timeline */}
                  {currentData.screenshot.thumbnails && currentData.screenshot.thumbnails.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                        {t('siteAudit.loadingProgress', 'Loading Progress')}
                      </p>
                      <div className="flex gap-1 overflow-x-auto pb-2">
                        {currentData.screenshot.thumbnails.slice(0, 10).map((thumb: { timing: number; timestamp: number; data: string }, idx: number) => (
                          <div key={idx} className="flex-shrink-0 text-center">
                            <img
                              src={thumb.data}
                              alt={`Frame ${idx + 1}`}
                              className="w-16 h-28 object-cover rounded border border-gray-200 dark:border-gray-700"
                            />
                            <span className="text-xs text-gray-400">
                              {(thumb.timing / 1000).toFixed(1)}s
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Core Web Vitals */}
            {currentData.coreWebVitals && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    {t('siteAudit.coreWebVitals', 'Core Web Vitals')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* LCP */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">LCP</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Largest Contentful Paint</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${getScoreColor(currentData.coreWebVitals.lcp.score)}`}>
                          {currentData.coreWebVitals.lcp.displayValue}
                        </p>
                        <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${getScoreBgColor(currentData.coreWebVitals.lcp.score)}`}
                            style={{ width: `${currentData.coreWebVitals.lcp.score}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* FID */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">FID</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">First Input Delay</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${getScoreColor(currentData.coreWebVitals.fid.score)}`}>
                          {currentData.coreWebVitals.fid.displayValue}
                        </p>
                        <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${getScoreBgColor(currentData.coreWebVitals.fid.score)}`}
                            style={{ width: `${currentData.coreWebVitals.fid.score}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* CLS */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">CLS</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Cumulative Layout Shift</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${getScoreColor(currentData.coreWebVitals.cls.score)}`}>
                          {currentData.coreWebVitals.cls.displayValue}
                        </p>
                        <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${getScoreBgColor(currentData.coreWebVitals.cls.score)}`}
                            style={{ width: `${currentData.coreWebVitals.cls.score}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Speed Index */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">Speed Index</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('siteAudit.visualSpeed', 'Visual Speed')}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${getScoreColor(currentData.coreWebVitals.speedIndex.score)}`}>
                          {currentData.coreWebVitals.speedIndex.displayValue}
                        </p>
                        <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${getScoreBgColor(currentData.coreWebVitals.speedIndex.score)}`}
                            style={{ width: `${currentData.coreWebVitals.speedIndex.score}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* TTFB */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">TTFB</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Time to First Byte</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${getScoreColor(currentData.coreWebVitals.ttfb.score)}`}>
                          {currentData.coreWebVitals.ttfb.displayValue}
                        </p>
                        <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${getScoreBgColor(currentData.coreWebVitals.ttfb.score)}`}
                            style={{ width: `${currentData.coreWebVitals.ttfb.score}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* TBT */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">TBT</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total Blocking Time</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${getScoreColor(currentData.coreWebVitals.tbt.score)}`}>
                          {currentData.coreWebVitals.tbt.displayValue}
                        </p>
                        <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${getScoreBgColor(currentData.coreWebVitals.tbt.score)}`}
                            style={{ width: `${currentData.coreWebVitals.tbt.score}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {auditResult.lighthouseVersion && (
                    <p className="text-xs text-gray-400 mt-4 text-center">
                      {t('siteAudit.poweredBy', 'Powered by')} Lighthouse {auditResult.lighthouseVersion}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Deep SEO Analysis */}
          {auditResult.seoAnalysis && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {t('siteAudit.seoAnalysis', 'SEO Analysis')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                  {/* Meta Tags */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      {t('siteAudit.metaTags', 'Meta Tags')}
                    </h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Title</span>
                          <Badge variant={auditResult.seoAnalysis.meta.titleLength >= 30 && auditResult.seoAnalysis.meta.titleLength <= 60 ? 'success' : 'warning'}>
                            {auditResult.seoAnalysis.meta.titleLength} {t('siteAudit.chars', 'chars')}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 break-words">
                          {auditResult.seoAnalysis.meta.title || <span className="text-red-500">{t('siteAudit.missing', 'Missing')}</span>}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Meta Description</span>
                          <Badge variant={auditResult.seoAnalysis.meta.descriptionLength >= 120 && auditResult.seoAnalysis.meta.descriptionLength <= 160 ? 'success' : 'warning'}>
                            {auditResult.seoAnalysis.meta.descriptionLength} {t('siteAudit.chars', 'chars')}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 break-words">
                          {auditResult.seoAnalysis.meta.description || <span className="text-red-500">{t('siteAudit.missing', 'Missing')}</span>}
                        </p>
                      </div>
                      {auditResult.seoAnalysis.meta.canonical && (
                        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Canonical</span>
                          <p className="text-sm text-gray-600 dark:text-gray-400 break-all">
                            {auditResult.seoAnalysis.meta.canonical}
                          </p>
                        </div>
                      )}
                      {/* Open Graph */}
                      {(auditResult.seoAnalysis.meta.ogTitle || auditResult.seoAnalysis.meta.ogDescription) && (
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <span className="text-sm font-medium text-blue-700 dark:text-blue-300 flex items-center gap-1">
                            Open Graph
                            <CheckCircle className="w-3 h-3" />
                          </span>
                          {auditResult.seoAnalysis.meta.ogTitle && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              <strong>og:title:</strong> {auditResult.seoAnalysis.meta.ogTitle}
                            </p>
                          )}
                          {auditResult.seoAnalysis.meta.ogDescription && (
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              <strong>og:description:</strong> {auditResult.seoAnalysis.meta.ogDescription.slice(0, 100)}...
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Headings Structure */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {t('siteAudit.headingsStructure', 'Headings Structure')}
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                        <span className="text-sm font-mono">H1</span>
                        <Badge variant={auditResult.seoAnalysis.headings.h1Count === 1 ? 'success' : auditResult.seoAnalysis.headings.h1Count === 0 ? 'danger' : 'warning'}>
                          {auditResult.seoAnalysis.headings.h1Count}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                        <span className="text-sm font-mono">H2</span>
                        <Badge variant="primary">{auditResult.seoAnalysis.headings.h2Count}</Badge>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                        <span className="text-sm font-mono">H3</span>
                        <Badge variant="primary">{auditResult.seoAnalysis.headings.h3Count}</Badge>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                        <span className="text-sm font-mono">H4</span>
                        <Badge variant="primary">{auditResult.seoAnalysis.headings.h4Count}</Badge>
                      </div>
                    </div>
                    {auditResult.seoAnalysis.headings.h1.length > 0 && (
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <span className="text-sm font-medium text-green-700 dark:text-green-300">H1 Content:</span>
                        {auditResult.seoAnalysis.headings.h1.map((h1, i) => (
                          <p key={i} className="text-sm text-gray-600 dark:text-gray-400 mt-1">"{h1}"</p>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Content & Links */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <Link2 className="w-4 h-4" />
                      {t('siteAudit.contentLinks', 'Content & Links')}
                    </h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="grid grid-cols-2 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                              {auditResult.seoAnalysis.content.wordCount}
                            </p>
                            <p className="text-xs text-gray-500">{t('siteAudit.words', 'Words')}</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                              {auditResult.seoAnalysis.content.textToHtmlRatio}%
                            </p>
                            <p className="text-xs text-gray-500">{t('siteAudit.textRatio', 'Text Ratio')}</p>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="grid grid-cols-2 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                              {auditResult.seoAnalysis.links.internalCount}
                            </p>
                            <p className="text-xs text-gray-500">{t('siteAudit.internalLinks', 'Internal Links')}</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                              {auditResult.seoAnalysis.links.externalCount}
                            </p>
                            <p className="text-xs text-gray-500">{t('siteAudit.externalLinks', 'External Links')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Images */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      {t('siteAudit.images', 'Images')}
                    </h4>
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            {auditResult.seoAnalysis.images.total}
                          </p>
                          <p className="text-xs text-gray-500">{t('common.total', 'Total')}</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {auditResult.seoAnalysis.images.withAlt}
                          </p>
                          <p className="text-xs text-gray-500">{t('siteAudit.withAlt', 'With Alt')}</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                            {auditResult.seoAnalysis.images.withoutAlt}
                          </p>
                          <p className="text-xs text-gray-500">{t('siteAudit.withoutAlt', 'Without Alt')}</p>
                        </div>
                      </div>
                    </div>
                    {auditResult.seoAnalysis.images.withoutAlt > 0 && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <span className="text-sm font-medium text-red-700 dark:text-red-300">
                          {t('siteAudit.imagesWithoutAltWarning', '{{count}} images need alt attributes', { count: auditResult.seoAnalysis.images.withoutAlt })}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Technical Checks */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      {t('siteAudit.technicalChecks', 'Technical Checks')}
                    </h4>
                    <div className="space-y-2">
                      {[
                        { key: 'usesHttps', label: 'HTTPS', value: auditResult.seoAnalysis.technical.usesHttps },
                        { key: 'hasViewport', label: 'Viewport Meta', value: auditResult.seoAnalysis.technical.hasViewport },
                        { key: 'hasCharset', label: 'Charset', value: auditResult.seoAnalysis.technical.hasCharset },
                        { key: 'hasLangAttribute', label: 'Lang Attribute', value: auditResult.seoAnalysis.technical.hasLangAttribute },
                        { key: 'hasStructuredData', label: 'Structured Data', value: auditResult.seoAnalysis.technical.hasStructuredData },
                        { key: 'hasFavicon', label: 'Favicon', value: auditResult.seoAnalysis.technical.hasFavicon },
                      ].map(check => (
                        <div key={check.key} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{check.label}</span>
                          {check.value ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* SEO Issues */}
                  {auditResult.seoAnalysis.issues && auditResult.seoAnalysis.issues.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        {t('siteAudit.seoIssues', 'SEO Issues')} ({auditResult.seoAnalysis.issues.length})
                      </h4>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {auditResult.seoAnalysis.issues.map((issue, idx) => (
                          <div
                            key={idx}
                            className={`p-2 rounded flex items-start gap-2 ${
                              issue.type === 'error'
                                ? 'bg-red-50 dark:bg-red-900/20'
                                : issue.type === 'warning'
                                ? 'bg-yellow-50 dark:bg-yellow-900/20'
                                : 'bg-blue-50 dark:bg-blue-900/20'
                            }`}
                          >
                            {issue.type === 'error' ? (
                              <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                            ) : issue.type === 'warning' ? (
                              <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                            ) : (
                              <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            )}
                            <span className="text-sm text-gray-700 dark:text-gray-300">{issue.message}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Load Time & Score */}
                <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:gap-6 text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {t('siteAudit.loadTime', 'Load Time')}: {auditResult.seoAnalysis.loadTime}ms
                    </span>
                    <span>
                      {t('siteAudit.pageSize', 'Page Size')}: {(auditResult.seoAnalysis.contentLength / 1024).toFixed(1)} KB
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{t('siteAudit.seoScore', 'SEO Score')}:</span>
                    <span className={`text-2xl font-bold ${getScoreColor(auditResult.seoAnalysis.score)}`}>
                      {auditResult.seoAnalysis.score}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Export Button */}
          <div className="flex justify-end">
            <Button variant="secondary" onClick={handleExportPDF} disabled={isExporting}>
              {isExporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {isExporting ? t('siteAudit.generating', 'Generating...') : t('siteAudit.exportPDF', 'Export PDF')}
            </Button>
          </div>

          {/* Categories */}
          <div className="space-y-4">
            {currentData.categories.map((category) => (
              <Card key={category.name}>
                <div
                  className="px-3 sm:px-6 py-2 sm:py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  onClick={() => toggleCategory(category.name)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                        {category.icon}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {category.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {category.issues.filter(i => i.severity !== 'passed').length} {t('siteAudit.issues', 'issues')} |{' '}
                          {category.issues.filter(i => i.severity === 'passed').length} {t('siteAudit.passed', 'passed')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                      <div className="text-right whitespace-nowrap">
                        <span className={`text-xl sm:text-2xl font-bold ${getScoreColor(category.score)}`}>
                          {category.score}
                        </span>
                        <span className="text-gray-400">/100</span>
                      </div>
                      {expandedCategories.has(category.name) ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {expandedCategories.has(category.name) && (
                  <CardContent className="pt-0 border-t border-gray-200 dark:border-gray-700">
                    <div className="space-y-4 mt-4">
                      {category.issues.map((issue) => (
                        <div
                          key={issue.id}
                          className={`p-4 rounded-lg border ${
                            issue.severity === 'critical'
                              ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                              : issue.severity === 'warning'
                              ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20'
                              : issue.severity === 'info'
                              ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20'
                              : 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {getSeverityIcon(issue.severity)}
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="font-medium text-gray-900 dark:text-white">
                                  {issue.title}
                                </h4>
                                {getSeverityBadge(issue.severity)}
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                {issue.description}
                              </p>
                              {issue.recommendation && (
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                  <strong>{t('siteAudit.recommendation', 'Recommendation')}:</strong> {issue.recommendation}
                                </p>
                              )}
                              {issue.affectedUrls && issue.affectedUrls.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    {t('siteAudit.affectedUrls', 'Affected URLs')}:
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {issue.affectedUrls.slice(0, 3).map((u, i) => (
                                      <code
                                        key={i}
                                        className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded"
                                      >
                                        {u}
                                      </code>
                                    ))}
                                    {issue.affectedUrls.length > 3 && (
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        +{issue.affectedUrls.length - 3} {t('common.more', 'more')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>

          {/* Audit Info */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm text-gray-500 dark:text-gray-400">
                <div className="flex items-center gap-4 min-w-0">
                  <span>
                    {t('siteAudit.auditedUrl', 'Audited URL')}: {' '}
                    <a
                      href={auditResult.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline break-all"
                    >
                      {auditResult.url}
                      <ExternalLink className="w-3 h-3 inline ml-1" />
                    </a>
                  </span>
                </div>
                <span>
                  {t('siteAudit.auditedAt', 'Audited at')}: {' '}
                  {new Date(auditResult.auditedAt).toLocaleString('pt-BR')}
                </span>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Empty State */}
      {!auditResult && !isLoading && (
        <Card>
          <CardContent className="text-center py-16">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t('siteAudit.startAnalysis', 'Start an analysis')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              {t('siteAudit.emptyState', 'Enter a site URL above to receive a complete SEO, performance, links, security and mobile compatibility analysis.')}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SiteAuditPage;
